import React, { useState, useEffect } from 'react';
import { Check, Gem, Sparkles, Zap, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { billing } from '../../mock';
import { usd, setTier } from '../../lib/format';
import { useAuth } from '../../context/AuthContext';
import { API } from '../../lib/api';
import { toast } from 'sonner';

export default function BillingTab() {
  const { user, setUser } = useAuth();
  const [tier, setTierState] = useState(user?.plan || 'free');

  useEffect(() => { setTier(tier); }, [tier]);

  const isPro = tier === 'pro';

  const changePlan = async (next) => {
    setTierState(next);
    try {
      const { data } = await API.put('/creator/settings', { plan: next });
      setUser(data);
      toast.success(next === 'pro' ? 'Upgraded to Pro — 0% fees unlocked 💎' : 'Switched to Free plan');
    } catch { toast.error('Could not update plan'); }
  };

  const toggle = () => changePlan(isPro ? 'free' : 'pro');

  return (
    <div className="space-y-6">
      {/* Tier toggle */}
      <div className="glass flex flex-col items-center justify-between gap-4 rounded-2xl p-5 sm:flex-row">
        <div className="flex items-center gap-3">
          <span className={`text-sm font-medium ${!isPro ? 'text-white' : 'text-muted-foreground'}`}>Free · 9% fee</span>
          <Switch checked={isPro} onCheckedChange={toggle} className="data-[state=checked]:bg-primary" />
          <span className={`text-sm font-medium ${isPro ? 'text-white' : 'text-muted-foreground'}`}>Pro · $39/mo — 0% fees</span>
        </div>
        <span className="rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-primary">
          Current: {isPro ? 'Pro' : 'Free'}
        </span>
      </div>

      {/* Plans */}
      <div className="grid gap-4 md:grid-cols-2">
        {billing.tiers.map((t) => {
          const active = tier === t.id;
          return (
            <div key={t.id} className={`relative overflow-hidden rounded-2xl p-5 transition-luxe ${active ? 'border border-primary/40 bg-primary/[0.06]' : 'glass'}`}>
              {t.id === 'pro' && (
                <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-primary/15 blur-2xl" />
              )}
              <div className="flex items-center gap-2">
                <h3 className="font-display text-lg font-bold text-white">{t.name}</h3>
                {t.id === 'pro' && <Gem className="h-4 w-4 text-primary" />}
                {active && <span className="ml-auto rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground">ACTIVE</span>}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">{t.tagline}</p>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-display text-3xl font-bold text-white">{t.price === 0 ? 'Free' : usd(t.price)}</span>
                {t.price > 0 && <span className="mb-1 text-sm text-muted-foreground">/mo</span>}
              </div>
              <p className="mt-1 text-xs font-medium text-primary">{t.fee}</p>

              <ul className="mt-4 space-y-2">
                {t.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-white/85">
                    <Check className="h-4 w-4 shrink-0 text-primary" /> {f}
                  </li>
                ))}
                {t.locked.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground/60">
                    <X className="h-4 w-4 shrink-0" /> {f}
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => changePlan(t.id)}
                disabled={active}
                className={`mt-5 w-full rounded-xl font-semibold ${t.id === 'pro' ? 'bg-primary text-primary-foreground hover:bg-primary/90 glow-lime' : 'bg-white/10 text-white hover:bg-white/15'} disabled:opacity-50`}
              >
                {active ? 'Current plan' : t.id === 'pro' ? 'Upgrade to Pro' : 'Downgrade'}
              </Button>
            </div>
          );
        })}
      </div>

      {/* AI Concierge Pro highlight */}
      <div className="glass flex items-center gap-4 rounded-2xl p-5">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/15">
          <Sparkles className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-display text-sm font-semibold text-white">AI Sales Concierge</p>
            <span className="flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary"><Gem className="h-3 w-3" /> PRO FEATURE</span>
          </div>
          <p className="text-xs text-muted-foreground">Auto-answers objections & recommends offers 24/7. {isPro ? 'Active on your store.' : 'Upgrade to unlock.'}</p>
        </div>
        {!isPro && (
          <Button onClick={() => changePlan('pro')} size="sm" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Zap className="mr-1 h-3.5 w-3.5" /> Unlock
          </Button>
        )}
      </div>

      {/* Invoices */}
      <div className="glass rounded-2xl p-5">
        <h3 className="mb-3 font-display text-sm font-semibold text-white">Billing history</h3>
        <div className="space-y-1">
          {billing.invoices.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between rounded-lg px-2 py-2.5 text-sm hover:bg-white/[0.03]">
              <span className="text-white/80">{inv.date}</span>
              <span className="text-muted-foreground">{inv.plan}</span>
              <span className="font-medium text-white">{usd(inv.amount)}</span>
              <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">{inv.status}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">Next billing: {billing.nextBilling} · {billing.paymentMethod.brand} •••• {billing.paymentMethod.last4}</p>
      </div>
    </div>
  );
}
